import React, { useState } from "react";
import { withTranslation } from "react-i18next";
import { Button, Card, Form, Grid } from "tabler-react";
import { toast } from "react-toastify";

const WaitlistControls = ({
    t,
    scheduleItem,
    date,
    waitlistEntries = [],
    onBulkPromote,
    onBulkCancel,
    onRefresh,
    isLoading = false,
    canManageWaitlist = true,
}) => {
    const [selectedEntries, setSelectedEntries] = useState([]);
    const [selectAll, setSelectAll] = useState(false);

    const handleSelectAll = () => {
        if (selectAll) {
            setSelectedEntries([]);
        } else {
            setSelectedEntries(waitlistEntries.map((entry) => entry.id));
        }
        setSelectAll(!selectAll);
    };

    const handleBulkPromote = async () => {
        if (selectedEntries.length === 0) {
            toast.warning(t("schedule.classes.waitlist.select_entries_first"));
            return;
        }

        try {
            await onBulkPromote(selectedEntries);
            setSelectedEntries([]);
            setSelectAll(false);
            toast.success(t("schedule.classes.waitlist.bulk_promote_success"));
        } catch (error) {
            toast.error(t("schedule.classes.waitlist.bulk_promote_error"));
            console.error("Error bulk promoting from waitlist:", error);
        }
    };

    const handleBulkCancel = async () => {
        if (selectedEntries.length === 0) {
            toast.warning(t("schedule.classes.waitlist.select_entries_first"));
            return;
        }

        try {
            await onBulkCancel(selectedEntries);
            setSelectedEntries([]);
            setSelectAll(false);
            toast.success(t("schedule.classes.waitlist.bulk_cancel_success"));
        } catch (error) {
            toast.error(t("schedule.classes.waitlist.bulk_cancel_error"));
            console.error("Error bulk cancelling waitlist:", error);
        }
    };

    const handleRefresh = () => {
        onRefresh();
        setSelectedEntries([]);
        setSelectAll(false);
    };

    return (
        <Card>
            <Card.Header>
                <Card.Title>
                    {t("schedule.classes.waitlist.controls_title")}
                </Card.Title>
            </Card.Header>
            <Card.Body>
                <Grid.Row>
                    <Grid.Col md={6}>
                        <div className="d-flex align-items-center gap-3">
                            <Form.Group>
                                <Form.Checkbox
                                    checked={selectAll}
                                    onChange={handleSelectAll}
                                    label={t(
                                        "schedule.classes.waitlist.select_all",
                                    )}
                                    disabled={waitlistEntries.length === 0}
                                />
                            </Form.Group>
                            <span className="text-muted small">
                                {selectedEntries.length}{" "}
                                {t("schedule.classes.waitlist.selected")} /{" "}
                                {waitlistEntries.length}{" "}
                                {t("schedule.classes.waitlist.total")}
                            </span>
                        </div>
                    </Grid.Col>
                    <Grid.Col md={6}>
                        <div className="d-flex justify-content-end gap-2">
                            <Button
                                color="secondary"
                                size="sm"
                                onClick={handleRefresh}
                                disabled={isLoading}
                                icon="refresh-cw"
                            >
                                {t("general.refresh")}
                            </Button>
                            {canManageWaitlist && (
                                <>
                                    <Button
                                        color="primary"
                                        size="sm"
                                        onClick={handleBulkPromote}
                                        disabled={
                                            isLoading ||
                                            selectedEntries.length === 0
                                        }
                                        icon="arrow-up"
                                    >
                                        {t(
                                            "schedule.classes.waitlist.bulk_promote",
                                        )}
                                    </Button>
                                    <Button
                                        color="danger"
                                        size="sm"
                                        onClick={handleBulkCancel}
                                        disabled={
                                            isLoading ||
                                            selectedEntries.length === 0
                                        }
                                        icon="x"
                                    >
                                        {t(
                                            "schedule.classes.waitlist.bulk_cancel",
                                        )}
                                    </Button>
                                </>
                            )}
                        </div>
                    </Grid.Col>
                </Grid.Row>

                <Grid.Row>
                    <Grid.Col>
                        <div className="mt-3 p-3 bg-light rounded">
                            <h6 className="mb-2">
                                {t("schedule.classes.waitlist.info_title")}
                            </h6>
                            <ul className="list-unstyled mb-0 small text-muted">
                                <li>
                                    •{" "}
                                    {t(
                                        "schedule.classes.waitlist.info_promote",
                                    )}
                                </li>
                                <li>
                                    •{" "}
                                    {t("schedule.classes.waitlist.info_cancel")}
                                </li>
                                <li>
                                    •{" "}
                                    {t(
                                        "schedule.classes.waitlist.info_automatic",
                                    )}
                                </li>
                                <li>
                                    •{" "}
                                    {t(
                                        "schedule.classes.waitlist.info_notifications",
                                    )}
                                </li>
                            </ul>
                        </div>
                    </Grid.Col>
                </Grid.Row>
            </Card.Body>
        </Card>
    );
};

export default withTranslation()(WaitlistControls);
